import cn from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { BusinessPriorities } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/businessPriorities/BusinessPriorities'
import { DescriptionQuality } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/descriptionQuality/DescriptionQuality'
import { FormFieldValidationLabel } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/formFieldValidationLabel/FormFieldValidationLabel'
import { KeyObjectives } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/keyObjectives/KeyObjectives'
import { KeyObjectivesTree } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/keyObjectivesTree/KeyObjectivesTree'
import { Name } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/name/Name'
import { isKeyObjectiveRelated } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/helpers/isKeyObjectiveRelated'
import { KeyInitiativeInitTypes } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/models/keyInitiativeInitTypes'
import { selectRtm } from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import {
  FormCheckBox,
  FormInput,
  FormSwitch,
  FormTextarea,
  updateFormDataField,
  useGetFormData,
} from 'cng-common/features/form'
import { ValidationType } from 'cng-common/features/form/configs'
import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Grid, Item } from 'core-components/library/grid'

import { ActivityType } from '../../../../../../commonComponents/editorComponents/components/activityType/ActivityType'
import { PeriodSelector } from '../../../../../../commonComponents/editorComponents/components/periodSelector/PeriodSelector'
import { PracticeBuilder } from '../../../../../../commonComponents/editorComponents/components/practiceBuilder/PracticeBuilder'
import { TRANSLATION_KEYS as tr } from '../../../../../../translations'
import {
  EXPAND_SINGLE_ROOT_TO_LEAVES,
  PRACTICE_DEVELOPMENT,
  SELECT_ONLY_LEAF_STRATEGIC_INITIATIVES,
} from '../../../configs'
import { IKeyInitiativeSectionProps } from '../../../models/IKeyInitiativeSectionProps'
import { PRACTICE_BUILDER_ROLES } from '../config'

import styles from './InitiativeSection.module.scss'

interface IInitiativeSectionProps extends IKeyInitiativeSectionProps {
  onDescriptionQualityCheck: (description?: string | null) => void
  onDescriptionValidate: (description: string) => Promise<string>
  isDescriptionQualityVisible: boolean
  availableBusinessPriorities: IKeyInitiativeSectionProps['businessPriorities']
  partnerMarket: string | null
  isBusinessPrioritiesPresent: boolean
  isBusinessPrioritiesRequired: boolean
}

export const InitiativeSection = ({
  initialData,
  partnerId,
  programType,
  isDataInsightPresent,
  isDataInsightTab = false,
  isAmplifyImpactInfoSection = false,
  keyObjectivesFilter,
  isPracticeDevelopment,
  keyObjectives,
  keyObjectiveTags,
  businessPriorities,
  fYear,
  initiativeFormKey,
  onDescriptionQualityCheck,
  onDescriptionValidate,
  isDescriptionQualityVisible,
  availableBusinessPriorities,
  partnerMarket,
  isBusinessPrioritiesPresent,
  isBusinessPrioritiesRequired,
}: IInitiativeSectionProps) => {
  /** -------------------- State ----------------------- */

  const [descriptionPlaceholder, setDescriptionPlaceholder] = useState(
    tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.descriptionPlaceholder
  )

  /** -------------------- Selectors ----------------------- */

  const translationMode = useSelector(selectTranslationMode)
  const rtm = useSelector(selectRtm)

  /** -------------------- Hooks ----------------------- */

  const dispatch = useAppDispatch()
  const t = useSafetyTranslation()
  const [{ formData }] = useGetFormData(initiativeFormKey, initialData)
  const formDataNameRef = useRef(formData.name)
  const formDataDescriptionRef = useRef(formData.description)

  formDataNameRef.current = formData.name
  formDataDescriptionRef.current = formData.description

  /** -------------------- Variables ----------------------- */

  const { keyObjectivesSettingsList } = useMemo(() => {
    return {
      keyObjectivesSettingsList: keyObjectives,
      keyObjectiveTagsSettingsList: keyObjectiveTags,
    }
  }, [keyObjectives, keyObjectiveTags])

  const selectedKeyObjectiveFromArray = useMemo(() => {
    return keyObjectives.filter((item) => item.key === formData.keyObjective)
  }, [keyObjectives, formData.keyObjective])

  const practiceDevelopmentType = useMemo(() => {
    const item = selectedKeyObjectiveFromArray[0]

    return item?.key?.toLowerCase().replace(/\s+/g, '') ?? ''
  }, [selectedKeyObjectiveFromArray])

  const isAutoCreatedKeyInitiative = useMemo(
    () => formData?.initType === KeyInitiativeInitTypes.ImportedInitiative,
    [formData?.initType]
  )

  const isTranslationMode = useMemo(() => translationMode === 'edit', [translationMode])

  const isLargeFormat = useMemo(() => {
    return programType === ProgramTypes.LargeFormat
  }, [programType])

  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    if (isPracticeDevelopment && !initialData?.name) {
      dispatch(
        updateFormDataField({
          field: 'name',
          value: PRACTICE_DEVELOPMENT,
          formKey: initiativeFormKey,
        })
      )
    }
  }, [isPracticeDevelopment, dispatch, initialData?.name, initiativeFormKey])

  useEffect(() => {
    if (isPracticeDevelopment && !initialData?.description) {
      const newDescription = t(
        `${tr.keyInitiativeEditor.practiceDevelopment.description}-${practiceDevelopmentType}`
      )
      // dispatch(updateFormDataField({field: 'description', value: newDescription, formKey: initiativeFormKey}))
      setDescriptionPlaceholder(newDescription)
    } else {
      setDescriptionPlaceholder(
        tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.descriptionPlaceholder
      )
    }
  }, [
    isPracticeDevelopment,
    practiceDevelopmentType,
    dispatch,
    t,
    initialData?.description,
    initiativeFormKey,
  ])

  useEffect(() => {
    if (isPracticeDevelopment || !formData?.practiceBuilder) {
      return
    }

    dispatch(
      updateFormDataField({
        field: 'practiceBuilder',
        value: '',
        formKey: initiativeFormKey,
      })
    )
  }, [dispatch, formData?.practiceBuilder, initiativeFormKey, isPracticeDevelopment])

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.root}>
      <RenderWhen condition={initialData?.isFromGlobalPartner}>
        <FormSwitch
          margin={'md'}
          formKey={initiativeFormKey}
          field={'isNotApplicable'}
          defaultValue={initialData?.isNotApplicable || false}
          label={
            formData?.isNotApplicable
              ? tr.keyInitiativeEditor.isApplicable
              : tr.keyInitiativeEditor.isNotApplicable
          }
        />
      </RenderWhen>

      <RenderWhen condition={!formData.isNotApplicable}>
        {/* Strategic Initiative */}

        <Grid noRootMargin direction="row">
          <Item xs={6} className={styles.labelItem}>
            <FormFieldValidationLabel
              text={
                tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.strategicInitiativeLabel
              }
              formKey={initiativeFormKey}
              fields={['keyObjective']}
            />
          </Item>
          <Item xs={6} className={styles.labelItem}>
            <Translation
              text={tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.secondaryIniativeLabel}
            />
          </Item>

          <Item xs={6}>
            <KeyObjectives
              formKey={initiativeFormKey}
              selectOnlyLeaves={SELECT_ONLY_LEAF_STRATEGIC_INITIATIVES}
              singleRootExpansion={EXPAND_SINGLE_ROOT_TO_LEAVES ? 'leaves' : 'root'}
              selectedValue={formData.keyObjective}
              isDisabled={
                isAutoCreatedKeyInitiative ||
                isAmplifyImpactInfoSection ||
                initialData?.isFromGlobalPartner
              }
              className={styles.formField}
              initialValue={
                isAmplifyImpactInfoSection ? 'Amplify Impact' : initialData?.keyObjective
              }
              keyObjectivesSettingsList={keyObjectivesSettingsList}
              programType={programType}
              keyObjectivesFilter={keyObjectivesFilter}
              caption="Breadcrumb"
              placeholder={
                tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup
                  .strategicInitiativePlaceholder
              }
            />
            {isTranslationMode && (
              <Translation
                text={
                  tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup
                    .strategicInitiativePlaceholder
                }
              />
            )}
          </Item>
          <Item xs={6}>
            <KeyObjectivesTree
              className={styles.formField}
              keyObjectivesSettingsList={keyObjectivesSettingsList}
              formKey={initiativeFormKey}
              initialValue={initialData?.keyObjectiveTags || []}
              programType={programType}
              rtm={rtm}
            />
            {isTranslationMode && (
              <Translation
                text={
                  tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup
                    .secondaryIniativePlaceholder
                }
              />
            )}
          </Item>
        </Grid>

        {/* Business Priorities */}

        <RenderWhen condition={isBusinessPrioritiesPresent}>
          <BusinessPriorities
            formKey={initiativeFormKey}
            initialValue={initialData?.businessPriorities}
            priorities={businessPriorities}
            availablePriorities={availableBusinessPriorities}
            market={partnerMarket}
            fYear={fYear}
            isDisabled={isAutoCreatedKeyInitiative || initialData?.isFromGlobalPartner}
            isRequired={isBusinessPrioritiesRequired}
          />
        </RenderWhen>

        {/* Initative Name & Period */}

        <Grid noRootMargin direction="row" className={styles.fieldsetContainer}>
          <Item xs={9} className={styles.labelItem}>
            <FormFieldValidationLabel
              text={tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.initiativeNameLabel}
              formKey={initiativeFormKey}
              fields={['name']}
            />
          </Item>
          <Item xs={3} className={styles.labelItem}>
            <FormFieldValidationLabel
              text={tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.periodLabel}
              formKey={initiativeFormKey}
              fields={['period']}
            />
          </Item>

          <Item xs={9} className={styles.formField}>
            <Name
              isDisabled={initialData?.isFromGlobalPartner || isPracticeDevelopment}
              formKey={initiativeFormKey}
              initialValue={
                isPracticeDevelopment && !initialData?.name
                  ? PRACTICE_DEVELOPMENT
                  : initialData?.name || ''
              }
              placeholder={
                tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.initiativeNamePlaceholder
              }
            />
            {isTranslationMode && (
              <Translation
                text={
                  tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.initiativeNamePlaceholder
                }
              />
            )}
          </Item>
          <Item xs={3} className={styles.periodSelector}>
            <PeriodSelector
              formKey={initiativeFormKey}
              isRequired
              isDisabled={isAutoCreatedKeyInitiative || initialData?.isFromGlobalPartner}
              className={styles.formField}
              initialValue={initialData?.period}
            />
          </Item>
        </Grid>

        {/* Description */}

        <Grid noRootMargin direction="row" className={styles.fieldsetContainer}>
          <Item xs={12} className={cn(styles.labelItem, styles.separatedItem)}>
            <FormFieldValidationLabel
              text={tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.descriptionLabel}
              formKey={initiativeFormKey}
              fields={['description']}
            />
          </Item>
          {(!isDescriptionQualityVisible || formData?.descriptionQuality) && (
            <Item xs={12}>
              <DescriptionQuality
                quality={formData?.descriptionQuality}
                description={formData?.description}
                isValidationActive={isDescriptionQualityVisible}
              />
            </Item>
          )}

          <Item xs={12}>
            <FormTextarea
              isDisabled={isAutoCreatedKeyInitiative || initialData?.isFromGlobalPartner}
              isRequired={!isAutoCreatedKeyInitiative}
              formKey={initiativeFormKey}
              field="description"
              defaultValue={initialData?.description || ''}
              onValidate={onDescriptionValidate}
              validationType={ValidationType.ONBLUR}
              onChange={(value) => {
                formDataDescriptionRef.current = value
              }}
              onBlur={() => onDescriptionQualityCheck(formDataDescriptionRef.current)}
              placeholder={descriptionPlaceholder}
              rows={5}
            />
            {isTranslationMode && isPracticeDevelopment && (
              <Translation
                text={`${tr.keyInitiativeEditor.practiceDevelopment.description}-${practiceDevelopmentType}`}
              />
            )}
            {isTranslationMode && !isPracticeDevelopment && (
              <Translation
                text={
                  tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.descriptionPlaceholder
                }
              />
            )}
          </Item>
        </Grid>

        {/* Partner Contact */}

        <Grid noRootMargin direction="row" className={styles.fieldsetContainer}>
          <Item xs={12} className={styles.labelItem}>
            <Translation
              text={tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.partnerContactLabel}
            />
          </Item>

          <Item xs={12}>
            <FormInput
              isDisabled={isAutoCreatedKeyInitiative || initialData?.isFromGlobalPartner}
              formKey={initiativeFormKey}
              field="owner"
              defaultValue={initialData?.owner || ''}
              type="text"
              placeholder={
                tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.partnerContactPlaceholder
              }
            />
            {isTranslationMode && (
              <Translation
                text={
                  tr.keyInitiativeEditor.keyInitiativeAndPeriodFieldGroup.partnerContactPlaceholder
                }
              />
            )}
          </Item>

          {initialData?.isFromGlobalPartner && (
            <Item xs={6}>
              <ActivityType
                isDisabled={initialData?.isFromGlobalPartner}
                formKey={initiativeFormKey}
                initialValue={initialData.activityType}
              />
            </Item>
          )}
        </Grid>

        {/* Practice Builder */}

        <RenderWhen condition={isPracticeDevelopment}>
          <Grid noRootMargin direction="row" className={styles.fieldsetContainer}>
            <Item xs={12} className={styles.labelItem}>
              <FormFieldValidationLabel
                text={tr.keyInitiativeEditor.practiceDevelopment.practiceBuilderLabel}
                formKey={initiativeFormKey}
                fields={['practiceBuilder']}
              />
            </Item>
            <Item xs={12}>
              <PracticeBuilder
                formKey={initiativeFormKey}
                className={styles.formField}
                initialValue={initialData?.practiceBuilder}
                partnerId={partnerId || ''}
                roles={PRACTICE_BUILDER_ROLES}
                strategicInitiative={formData?.keyObjective}
                isDisabled={isAutoCreatedKeyInitiative || initialData?.isFromGlobalPartner}
                placeholder={tr.keyInitiativeEditor.practiceDevelopment.practiceBuilderPlaceholder}
              />
              {isTranslationMode && (
                <Translation
                  text={tr.keyInitiativeEditor.practiceDevelopment.practiceBuilderPlaceholder}
                />
              )}
            </Item>
          </Grid>
        </RenderWhen>

        {!isAutoCreatedKeyInitiative && !isPracticeDevelopment && (
          <Grid noRootMargin direction="row" className={styles.fieldsetContainer}>
            {!isLargeFormat && isDataInsightPresent && (
              <>
                <Item xs={12}>
                  <div className={styles.checkboxItem}>
                    <FormCheckBox
                      isDisabled={
                        isKeyObjectiveRelated(keyObjectivesSettingsList, initialData) ||
                        isDataInsightTab ||
                        initialData?.isFromGlobalPartner
                      }
                      formKey={initiativeFormKey}
                      field="isDataInsightRelated"
                      defaultValue={initialData?.isDataInsightRelated || isDataInsightTab}
                    />
                    <Translation
                      text={tr.keyInitiativeEditor.checkboxesFieldGroup.dataInsightRelatedLabel}
                    />
                  </div>
                </Item>
              </>
            )}
          </Grid>
        )}

        {isAutoCreatedKeyInitiative && (
          <Grid noRootMargin direction="row" className={styles.fieldsetContainer}>
            <Item xs={12} className={styles.sectionHeading}>
              <Translation
                text={tr.keyInitiativeEditor.capabilitiesPillarInitiativeFieldGroup.groupHint}
              />
            </Item>

            <Item xs={12}>
              {isTranslationMode && (
                <Translation
                  text={
                    tr.keyInitiativeEditor.capabilitiesPillarInitiativeFieldGroup.goalDescription
                  }
                />
              )}
              {!isTranslationMode && (
                <FormTextarea
                  isDisabled={isAutoCreatedKeyInitiative || initialData?.isFromGlobalPartner}
                  formKey={initiativeFormKey}
                  field="goalDescription"
                  defaultValue={initialData?.goalDescription || ''}
                  label={
                    tr.keyInitiativeEditor.capabilitiesPillarInitiativeFieldGroup.goalDescription
                  }
                />
              )}
            </Item>

            <Item xs={12}>
              <FormCheckBox
                isDisabled={isAutoCreatedKeyInitiative || initialData?.isFromGlobalPartner}
                formKey={initiativeFormKey}
                field="isAchieved"
                defaultValue={initialData?.isAchieved || false}
                label={tr.keyInitiativeEditor.capabilitiesPillarInitiativeFieldGroup.isAchieved}
              />
            </Item>
            <Item xs={12}>
              {isTranslationMode && (
                <Translation
                  text={
                    tr.keyInitiativeEditor.capabilitiesPillarInitiativeFieldGroup.proofOfExecution
                  }
                />
              )}
              {!isTranslationMode && (
                <FormTextarea
                  isDisabled={isAutoCreatedKeyInitiative || initialData?.isFromGlobalPartner}
                  formKey={initiativeFormKey}
                  field="proofOfExecution"
                  defaultValue={initialData?.proofOfExecution || ''}
                  label={
                    tr.keyInitiativeEditor.capabilitiesPillarInitiativeFieldGroup.proofOfExecution
                  }
                  className={styles.formField}
                />
              )}
            </Item>
          </Grid>
        )}
      </RenderWhen>
    </div>
  )
}
